/**
 * Text measurers. The engine only ever needs the width of a single line; the
 * canvas-backed measurer caches by (font css, text) because the engine runs on
 * every commit and texts rarely change.
 */

import type { ResolvedFont, TextMeasurer } from './types';

type Canvas2D = Pick<CanvasRenderingContext2D, 'font' | 'measureText'>;

/** Strip the `/line-height` part: some canvas implementations reject it. */
function canvasFont(font: ResolvedFont): string {
  return font.css.replace(/\/\d+(\.\d+)?px/, '');
}

/**
 * Measures with a 2D canvas context (`OffscreenCanvas` when available, else a
 * detached `<canvas>`). The context is created lazily on the first
 * measurement, so importing this module never touches the DOM. When no canvas
 * is available (Node), it falls back to the monospace estimate.
 */
export class CanvasTextMeasurer implements TextMeasurer {
  /** Set by the renderer: measures a `host` element's content in the DOM. */
  measureHost?: TextMeasurer['measureHost'];
  private context: Canvas2D | null | undefined;
  private readonly cache = new Map<string, number>();
  private readonly fallback = new MonospaceTextMeasurer();
  private currentFont = '';
  private static readonly MAX_CACHE = 20_000;

  measureWidth(text: string, font: ResolvedFont): number {
    if (text.length === 0) return 0;
    const key = `${font.css}\u0000${font.monospacedDigit ? 'T' : ''}\u0000${text}`;
    const cached = this.cache.get(key);
    if (cached !== undefined) return cached;
    const context = this.acquireContext();
    let width: number;
    if (context) {
      const css = canvasFont(font);
      if (this.currentFont !== css) {
        context.font = css;
        this.currentFont = css;
      }
      // A canvas has no `font-variant-numeric`: tabular digits are measured
      // as the font's widest digit, which is the width a tabular figure takes.
      width = context.measureText(font.monospacedDigit ? text.replace(/[0-9]/g, this.widestDigit(context, css)) : text).width;
    } else {
      width = this.fallback.measureWidth(text, font);
    }
    if (this.cache.size >= CanvasTextMeasurer.MAX_CACHE) this.cache.clear();
    this.cache.set(key, width);
    return width;
  }

  private readonly widestDigits = new Map<string, string>();

  /** The font's widest digit (its `font` already set on `context`). */
  private widestDigit(context: Canvas2D, css: string): string {
    const known = this.widestDigits.get(css);
    if (known !== undefined) return known;
    let widest = '0';
    let max = -1;
    for (const digit of '0123456789') {
      const width = context.measureText(digit).width;
      if (width > max) {
        max = width;
        widest = digit;
      }
    }
    this.widestDigits.set(css, widest);
    return widest;
  }

  /** Drop cached widths (call when web fonts finish loading). */
  clearCache(): void {
    this.cache.clear();
    this.widestDigits.clear();
  }

  private acquireContext(): Canvas2D | null {
    if (this.context !== undefined) return this.context;
    this.context = null;
    try {
      const g = globalThis as unknown as {
        OffscreenCanvas?: new (w: number, h: number) => { getContext(id: '2d'): Canvas2D | null };
        document?: { createElement(tag: 'canvas'): { getContext(id: '2d'): Canvas2D | null } };
      };
      if (typeof g.OffscreenCanvas === 'function') {
        this.context = new g.OffscreenCanvas(1, 1).getContext('2d');
      }
      if (!this.context && g.document && typeof g.document.createElement === 'function') {
        this.context = g.document.createElement('canvas').getContext('2d');
      }
    } catch {
      this.context = null;
    }
    return this.context;
  }
}

/**
 * Deterministic measurer for tests: 0.6em per code point, 0.3em for a space.
 */
export class MonospaceTextMeasurer implements TextMeasurer {
  measureWidth(text: string, font: ResolvedFont): number {
    let width = 0;
    for (const ch of text) width += ch === ' ' ? 0.3 * font.size : 0.6 * font.size;
    return width;
  }
}
